import React, { useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faSave,
    faInfoCircle,
    faMoneyBillWave
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { usePrazo, useAlterarPrazo } from 'hooks/usePrazo';
import { AlterarPrazoDto } from 'services/prazoService';
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useToast } from "providers/ToastProvider";
import BotaoExcluirPrazo from "components/prazo/BotaoExcluirPrazo";
import BotaoReativarPrazo from "components/prazo/BotaoReativarPrazo";

// Define breadcrumbs for the edit prazo page
const prazoBreadcrumbItems = (prazo?: any) => [
    {
        label: "Retaguarda",
        url: "#!",
    },
    {
        label: "Cadastros",
        url: "#!",
    },
    {
        label: "Prazos",
        url: "/cadastros/prazos",
    },
    {
        label: prazo ? `Editar ${prazo.parcela}x` : "Editar",
        active: true,
    },
];

const EditarPrazo: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { showToast } = useToast();

    // React Hook Form setup
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<AlterarPrazoDto>({
        defaultValues: {
            descricao: '',
        },
        mode: 'onSubmit',
    });

    // Hooks
    const prazoQuery = usePrazo(parseInt(id!), !!id);
    const alterarMutation = useAlterarPrazo();

    const prazo = prazoQuery.data;

    // Carregar dados quando disponíveis
    useEffect(() => {
        if (prazo) {
            reset({
                descricao: prazo.descricao,
            });
        }
    }, [prazo, reset]);

    const onSubmit = async (data: AlterarPrazoDto) => {
        try {
            await alterarMutation.mutateAsync({
                id: parseInt(id!),
                data
            });

            // Navigate with success message
            navigate('/cadastros/prazos');
            showToast('Prazo atualizado com sucesso', 'success');
        } catch (error) {
            console.error('Erro ao alterar prazo:', error);
        }
    };

    if (prazoQuery.isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </div>
        );
    }

    if (prazoQuery.isError || !prazo) {
        return (
            <div>
                <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle">
                    <div className="d-flex align-items-center text-danger-emphasis">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Prazo não encontrado.
                    </div>
                </Alert>
            </div>
        );
    }

    return (
        <div>
            <PageBreadcrumb items={prazoBreadcrumbItems(prazo)} />

            <Form
                noValidate
                className="mb-9"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h2 className="mb-0">Editar Prazo {prazo.parcela}x</h2>
                            <Badge bg={prazo.isDeleted ? 'danger' : 'success'}>
                                {prazo.isDeleted ? 'Inativo' : 'Ativo'}
                            </Badge>
                        </div>
                        <h5 className="text-body-tertiary fw-semibold">
                            Altere as informações do prazo
                        </h5>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {prazo.isDeleted ? (
                            <BotaoReativarPrazo
                                prazo={prazo}
                                variant="success"
                            />
                        ) : (
                            <BotaoExcluirPrazo
                                prazo={prazo}
                                variant="danger"
                            />
                        )}
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={alterarMutation.isPending}
                        >
                            <FontAwesomeIcon icon={faSave} className="me-2" />
                            {alterarMutation.isPending ? "Salvando..." : "Salvar Alterações"}
                        </Button>
                    </div>
                </div>

                {/* Informações Básicas Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faClock} className="me-2" />
                            Informações Básicas
                        </h5>

                        {alterarMutation.isError && (
                            <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-4">
                                <div className="text-danger-emphasis">
                                    <strong>Erro:</strong> Não foi possível alterar o prazo.
                                    Tente novamente.
                                </div>
                            </Alert>
                        )}

                        <div className="row g-3">
                            {/* Parcela (readonly) */}
                            <div className="col-md-3">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Parcelas
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={`${prazo.parcela}x`}
                                        readOnly
                                        className="bg-body-secondary"
                                    />
                                    <Form.Text className="text-body-secondary">
                                        Quantidade de parcelas (não editável)
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Descrição */}
                            <div className="col-md-9">
                                <Form.Group>
                                    <Form.Label className="text-body-emphasis fw-semibold">
                                        Descrição *
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register('descricao', {
                                            required: 'Descrição é obrigatória',
                                            maxLength: {
                                                value: 100,
                                                message: 'Descrição deve ter no máximo 100 caracteres'
                                            },
                                            validate: (value) => {
                                                if (!value?.trim()) {
                                                    return 'Descrição não pode estar vazia';
                                                }
                                                return true;
                                            }
                                        })}
                                        isInvalid={!!errors.descricao}
                                        placeholder="Ex: À Vista, 2x sem juros, 12x..."
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.descricao?.message}
                                    </Form.Control.Feedback>
                                    <Form.Text className="text-body-secondary">
                                        Nome que identificará este prazo
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Formas de Pagamento Associadas Card */}
                {prazo.formasPagamento.length > 0 && (
                    <Card className="mb-4">
                        <Card.Body>
                            <h5 className="mb-3">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
                                Formas de Pagamento Associadas ({prazo.formasPagamento.length})
                            </h5>

                            <div className="row g-2">
                                {prazo.formasPagamento.map((fp) => (
                                    <div key={fp.id} className="col-md-4 col-sm-6">
                                        <div className="border rounded p-2 d-flex align-items-center justify-content-between">
                                            <div>
                                                <div className="fw-medium text-body-emphasis">
                                                    {fp.descricao}
                                                </div>
                                                <div className="text-body-secondary small">
                                                    {fp.tipo}
                                                </div>
                                            </div>
                                            <Badge bg={fp.isDeleted ? 'danger' : 'success'} className="ms-2">
                                                {fp.isDeleted ? 'Inativa' : 'Ativa'}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                <div className="d-flex justify-content-between mt-4">
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => navigate("/cadastros/prazos")}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={alterarMutation.isPending}
                    >
                        <FontAwesomeIcon icon={faSave} className="me-2" />
                        {alterarMutation.isPending ? "Salvando..." : "Salvar Alterações"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default EditarPrazo;